import { FC, useCallback, useEffect, useState } from "react";
import { ListUtil } from "src/framework/commons/ListUtil";
import { ValidateUtil } from "src/framework/commons/ValidateUtil";
import { InputCEP } from "src/framework/components/forms/inputs/InputCEP";
import { InputPhone } from "src/framework/components/forms/inputs/InputPhone";
import api, { STATUS_CODE } from "src/framework/providers/api";
import { useObservable } from "src/framework/store/storeUtil";
import { cidades$, estados$ } from "src/store/global.store";
import { Cidade } from "../geral/RegioesDataTable/types";
import { Tooltip } from "@mui/material";
import { notifierStore } from "src/framework/store/NotifierStore";
import { InputText } from "src/framework/components/forms/inputs/InputText";
import { InputCPFCNPJ } from "src/framework/components/forms/inputs/InputCPFCNPJ";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";
import { parseCEP } from "src/framework/commons/masks";
import { NOME_CLIENTE_MAX_LENGTH } from "./constants";

interface FormClientProperties {
    formState: any;
    onChangeValue: (formState: any) => void
    errorState: string[];
    onChangeError: (errorState: any) => void
}

const CLIENTE_PROPOSTA = "cliente_proposta"
const EMAIL_PROPOSTA = "email_proposta"
const CIDADE_ID = "cidade_id"

const FormCliente: FC<FormClientProperties> = ({ formState, onChangeValue, errorState, onChangeError }) => {
    const estadoListState = useObservable(estados$);
    const cidadeListState = useObservable(cidades$);

    const [estadoSelecionado, setEstadoSelecionado] = useState<string>("")

    useEffect(()=>{
        if (formState?.estado) {
            setEstadoSelecionado(formState.estado.id)
        }
    }, [formState?.estado])

    const validateEmail = (email: string) => {
        return !!String(email)
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          );
    };

    const requestCep = useCallback(async(state: any) => {
        const formattedValue = state.cep.replace(/\D/g, "")
        if (formattedValue.length < 8) state.cep_encontrado = false
        if (formattedValue.length === 8) {
            const result = await api.get({
                url: `/cep/${formattedValue}`
            })
            
            if (result.statusCode !== STATUS_CODE.OK)   {
                state.estado = null
                state.cidade = null
                state.endereco_proposta = ''
                state.bairro = ''
                state.cep_encontrado = false
                state.cep = '';
                setEstadoSelecionado('');

                notifierStore.setNotifier({
                    title: "Aviso",
                    content: 'CEP não cadastrado. Preencha os campos do endereço manualmente.',
                    timeOut: 3500,
                    onClose: () => {
                        notifierStore.setOpen(false)
                    }
                })
                notifierStore.setOpen(true);
            } else {
                const { data } = result
                const estado = estadoListState?.data?.find((estado: any) => estado.id === data.uf)
                if (estado) {
                    state.cep_encontrado = true;


                    state.isTrocaUf = false;
                    state.tarifa_concessionaria_id = state.tarifa_concessionaria_id_original;
                    state.preco_kwh_com_imposto = state.preco_kwh_com_imposto_original;


                    if(estado.id !== state?.estado_original?.id){
                        state.isTrocaUf = true;
                    }
                }
                state.estado = estado
                let cidade = cidadeListState?.data?.find((cidade: Cidade) => cidade.id === data.ibge)
                if (!cidade && data) cidade = { id: data?.ibge, nome: data?.cidade, estado_id: data?.uf }
                state.cidade = cidade
                state.bairro = data.bairro || ''
                state.endereco_proposta = data.endereco || ''
                state.numero = ''
                setEstadoSelecionado(data?.uf);
            }
        }
        onChangeValue(state)
    }, [onChangeValue, cidadeListState, estadoListState])

    return (
        <>
            <div className="row">
                <div className="L12">
                    <InputText label="Título do projeto" fullWidth
                        value={formState?.titulo}
                        defaultValue={formState?.titulo}
                        maxLength={100}
                        onChange={(e: any) => {
                            const state = { ...formState }
                            state.titulo = e.target.value
                            onChangeValue(state)
                        }}
                    />
                </div>
                <div className="X12 L4">
                    <InputText label="* Nome do cliente" fullWidth
                        value={formState?.cliente_proposta}
                        error={errorState.includes(CLIENTE_PROPOSTA)}
                        maxLength={NOME_CLIENTE_MAX_LENGTH}
                        defaultValue={formState?.cliente_proposta}
                        onChange={(e: any) => {
                            const state = { ...formState }
                            state.cliente_proposta = e.target.value
                            onChangeValue(state)
                        }}
                        onKeyUp={(e: any) => {
                            if (e.target.value !== "") {
                                const idxError = errorState.findIndex((es) => es === CLIENTE_PROPOSTA)
                                if (idxError > -1) {
                                    errorState.splice(idxError, 1)
                                    onChangeError(errorState)
                                }
                            }
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputText label="E-mail" fullWidth
                        value={formState?.email_proposta}
                        error={errorState.includes(EMAIL_PROPOSTA)}
                        helperText={errorState.includes(EMAIL_PROPOSTA) ? "E-mail inválido" : ''}
                        inputProps={{
                            autocomplete: 'off',
                        }}
                        onChange={(e: any) => {
                            const state = { ...formState }
                            state.email_proposta = (String(e.target.value) || '').trim()
                            onChangeValue(state)
                        }}
                        onKeyDown={(e: any) => {
                            const spaceRegex = /\s/g
                            e.target.value = e.target.value.replace(spaceRegex, '')
                        }}
                        onKeyUp={(e: any) => {
                            let _errorState = [...errorState]
                            if (e.target.value !== "") {
                                _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                                if (ValidateUtil.isValidEmail(e.target.value) && validateEmail(e.target.value)) {
                                    _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                                } else {
                                    _errorState = ListUtil.addItem(_errorState, EMAIL_PROPOSTA)
                                }
                            }else{
                                _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                            }
                            onChangeError(_errorState)
                        }}
                    />
                </div>

                <Tooltip title='Necessário preencher caso queira simular um financiamento na etapa de Finalização.'>
                    <div className="X12 L4">
                        <InputCPFCNPJ
                            label="CPF/CNPJ"
                            maxLength={18}
                            value={formState?.cpf_cnpj_proposta}
                            onChangeValue={(newValue) => {
                                const { value } = newValue;
                                const state = { ...formState }
                                state.cpf_cnpj_proposta = value
                                onChangeValue(state)
                            }}
                        />
                    </div>
                </Tooltip>
                <div className="X12 L4">
                    <InputCEP
                        label="CEP"
                        value={formState.cep || ''}
                        onChange={async(e: any) => {
                            const { value = '' } = e.target;
                            const state = { ...formState }
                            state.cep = parseCEP(value)
                            onChangeValue(state)
                            requestCep(state)

                            if (!value) {
                                state.cep = '';
                                state.endereco_proposta = '';
                                state.numero = '';
                                state.bairro = '';
                                onChangeValue(state)
                            }
                        }}
                        />
                </div>

                <div className="X12 L4">
                    <InputComboBoxList
                        id={`listagem-verticais-estado-cb`}
                        key={`listagem-verticais-estado-cb`}
                        label={"* UF"}
                        disabled={formState.cep_encontrado}
                        textField="nome"
                        valueField="id"
                        options={estadoListState?.data || []}
                        value={formState?.estado || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option.id} - ${option.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option.id} - ${option.nome}`
                        }}
                        onChange={(value: any) => {
                            const state = { ...formState };
                            if (value) {
                                state.isTrocaUf = false;
                                state.tarifa_concessionaria_id = state.tarifa_concessionaria_id_original;
                                state.preco_kwh_com_imposto = state.preco_kwh_com_imposto_original;

                                if(value.id !== state?.estado_original?.id){
                                    state.isTrocaUf = true;
                                }

                                state.estado = value;
                                state.cidade = undefined;
                                state.cidade_id = undefined;

                                setEstadoSelecionado(String(value.id))

                            } else {
                                state.estado = null;
                                setEstadoSelecionado('')
                            }
                            onChangeValue(state)
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputComboBoxList
                        id={`listagem-verticais-cidade-cb`}
                        key={`listagem-verticais-cidade-cb`}
                        disabled={formState.cep_encontrado || estadoSelecionado === ""}
                        label={"* Cidade"}
                        textField="nome"
                        valueField="id"
                        error={errorState.includes(CIDADE_ID)}
                        options={cidadeListState?.data?.filter((c: Cidade) => c.estado_id === estadoSelecionado) || []}
                        value={formState?.cidade || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option?.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option?.nome}`
                        }}
                        onChange={(value: Cidade) => {
                            const state = { ...formState };
                            if (value) {
                                const idxError = errorState.findIndex((es) => es === CIDADE_ID)
                                if (idxError > -1) {
                                    errorState.splice(idxError, 1)
                                    onChangeError([...errorState])
                                }
                                state.cidade = value;
                                state.cidade_id = value.id;
                            } else {
                                state.cidade = null;
                                state.cidade_id = undefined;
                            }

                            onChangeValue(state)
                        }}
                    />
                </div>

                <div className="X12">
                    <InputText label="Rua"
                        fullWidth
                        value={formState?.endereco_proposta}
                        onChange={(e: any) => {
                            const state: any = { ...formState };
                            state.endereco_proposta = e.target.value
                            if(!e.target.value.trim()){
                                state.bairro = "";
                                state.numero = "";
                            }
                            onChangeValue(state)
                        }}
                    />
                </div>
                <div className="X12 L4">
                    <InputText
                        disabled={!formState.endereco_proposta.trim()}
                        fullWidth
                        maxLength={10}
                        label="Número"
                        name="numero"
                        onChange={(e: any) => {
                            const state: any = { ...formState };
                            state.numero = e.target.value
                            onChangeValue(state)
                        }}
                        value={formState.numero || ""}
                    />
                </div>

                <div className="X12 L4">
                    <InputText label="Bairro"
                        disabled={!formState.endereco_proposta.trim()}
                        fullWidth
                        value={formState?.bairro || ""}
                        onChange={(e: any) => {
                            const state: any = { ...formState };
                            state.bairro = e.target.value
                            onChangeValue(state)
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputPhone
                        label="Telefone"
                        value={formState?.telefone_proposta}
                        onChangeValue={(newValue) => {
                            const { value } = newValue;
                            const state = { ...formState }
                            state.telefone_proposta = value
                            onChangeValue(state)
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export { FormCliente }